import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button-simple';
import { LogOut, Settings, UserCircle, ChevronDown } from 'lucide-react';

export function Navigation({ user, onLogout, onLogin, onSignUp }) {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: '/home', label: 'Home' },
    { path: '/farmers', label: 'Farmers' },
    { path: '/processors', label: 'Processors' },
    { path: '/map', label: 'Map' },
    { path: '/services', label: 'Services' },
    { path: '/research', label: 'Research' },
    { path: '/analytics', label: 'Analytics' },
    { path: '/about', label: 'About' },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getInitials = (firstName, lastName, username) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (username) {
      return username.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username || 'User';
  };

  // All logged-in users use left sidebars — no top navbar needed
  if (user) return null;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-950 via-amber-900 via-amber-800 via-amber-700 to-amber-950 shadow-xl backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left spacer for centering */}
          <div className="hidden md:block w-48"></div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex items-center justify-center flex-1">
            {user && (
              <div className="flex items-baseline space-x-2">
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `px-4 py-2 rounded-full transition-all duration-300 transform hover:scale-105 text-sm font-medium ${
                        isActive
                          ? 'bg-amber-700 text-white shadow-lg'
                          : 'text-amber-100 hover:bg-amber-800 hover:text-white hover:shadow-md'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* Right side - Auth/Profile */}
          <div className="hidden md:flex items-center space-x-3">
              {user ? (
                <div className="relative" ref={menuRef}>
                  {/* User Profile Button */}
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 text-amber-100 hover:bg-amber-800 rounded-full px-4 py-2 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                  >
                    {/* Avatar */}
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover border-2 border-amber-200"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white text-sm font-bold border-2 border-amber-200">
                        {getInitials(user.firstName, user.lastName, user.username)}
                      </div>
                    )}
                    
                    {/* User Info */}
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-medium">
                        {getDisplayName(user)}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        user.role === 'admin' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-blue-600 text-white'
                      }`}>
                        {user.role}
                      </span>
                    </div>
                    
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50 animate-slide-down">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/profile');
                        }}
                        className="w-full px-4 py-2 text-left text-gray-700 hover:bg-amber-50 flex items-center space-x-2 transition-all duration-200 rounded-lg mx-1"
                      >
                        <UserCircle className="w-4 h-4" />
                        <span>View Profile</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/settings');
                        }}
                        className="w-full px-4 py-2 text-left text-gray-700 hover:bg-amber-50 flex items-center space-x-2 transition-all duration-200 rounded-lg mx-1"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </button>
                      
                      <hr className="my-1 border-gray-200" />
                      
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          onLogout();
                        }}
                        className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center space-x-2 transition-all duration-200 rounded-lg mx-1"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={onLogin}
                    variant="outline"
                    className="text-amber-100 border-amber-600 hover:bg-amber-800 hover:text-white rounded-full px-6 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                  >
                    Login
                  </Button>
                  <Button
                    onClick={onSignUp}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                  >
                    Sign Up
                  </Button>
                </div>
              )}
          </div>
          
          {/* Mobile menu */}
          <div className="md:hidden flex items-center space-x-2">
            {user && (
              <select
                value={location.pathname}
                onChange={(e) => navigate(e.target.value)}
                aria-label="Navigation menu"
                className="bg-amber-800 text-white rounded-full px-3 py-2 border border-amber-700 text-sm transition-all duration-200 hover:bg-amber-700"
              >
                {navItems.map((item) => (
                  <option key={item.path} value={item.path}>
                    {item.label}
                  </option>
                ))}
              </select>
            )}
            
            {user ? (
              <div className="flex items-center space-x-2">
                {/* Mobile User Avatar */}
                <button
                  onClick={() => navigate('/profile')}
                  className="flex items-center space-x-1"
                  aria-label="View profile"
                >
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt="Profile"
                      className="w-6 h-6 rounded-full object-cover border border-amber-200"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white text-xs font-bold border border-amber-200">
                      {getInitials(user.firstName, user.lastName, user.username)}
                    </div>
                  )}
                  <span className="text-amber-100 text-sm">{getDisplayName(user).split(' ')[0]}</span>
                </button>
                
                <Button
                  variant="ghost"
                  onClick={() => { onLogout(); navigate('/'); }}
                  className="text-amber-100 hover:bg-amber-800 p-1"
                  aria-label="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-1">
                <Button
                  onClick={onLogin}
                  variant="outline"
                  className="text-amber-100 border-amber-600 hover:bg-amber-800 text-xs px-2 py-1"
                >
                  Login
                </Button>
                <Button
                  onClick={onSignUp}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1"
                >
                  Sign Up
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

Navigation.propTypes = {
  user: PropTypes.shape({
    username: PropTypes.string,
    role: PropTypes.string,
  }),
  onLogout: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onSignUp: PropTypes.func.isRequired,
};